import { useEffect, useRef, useState } from "react";

import { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEventListener } from "usehooks-ts";

import { NO_FOLDER_FILTER } from "src/api";
import {
  FlowFolderListItem,
  FlowFolderListResponse,
} from "src/clients/flow-api";
import { toastActions } from "src/design-system/Toast/utils";
import { AddFolder } from "src/flowsOverview/v2/AddFolder";
import { Folder, FolderVariant } from "src/flowsOverview/v2/Folder";
import {
  FolderListSkeletonDefault,
  FolderListSkeletonSidebar,
} from "src/flowsOverview/v2/FolderListSkeleton";
import { DeleteFolderModal } from "src/flowsOverview/v2/folderModals/DeleteFolderModal";
import { EditFolderModal } from "src/flowsOverview/v2/folderModals/EditFolderModal";
import {
  useCreateFolder,
  useDeleteFolder,
  useEditFolder,
} from "src/flowsOverview/v2/folderQueries";
import { useLatestViewedFolder } from "src/flowsOverview/v2/useLatestViewedFolder";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import {
  getBaseUrl,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";

const listVariantStyles = {
  default: {
    container: "decideScrollbar h-auto min-h-0 w-70",
    list: "col-span-1 flex flex-col gap-y-3",
  },
  sidebar: {
    container:
      "flex flex-col h-full gap-y-2 ml-3 pl-1 border-l border-gray-200",
    list: "flex flex-col gap-y-2 w-54 overflow-y-scroll overscroll-auto decideScrollbarThin",
  },
} as const;

type Props = {
  variant?: FolderVariant;
  organizationId: string;
  workspaceId: string;
  folderResult: UseQueryResult<FlowFolderListResponse, Error>;
};

export const FolderList: React.FC<Props> = ({
  variant = "default",
  organizationId,
  workspaceId,
  folderResult,
}) => {
  const [selectedFolderParam, setSelectedFolderParam] =
    useFolderIdSearchParam();
  const navigate = useNavigate();
  const { flowFolders } = useCapabilities();
  const createFolderMutations = useCreateFolder();
  const editFolderMutations = useEditFolder(workspaceId);
  const deleteFolderMutations = useDeleteFolder(workspaceId);
  const userId = useCurrentUserId();

  const [latestViewedFolder, setLatestViewedFolder] = useLatestViewedFolder({
    userId: userId!,
    workspaceId,
  });

  const [createFolderModalOpen, setCreateFolderModalOpen] =
    useState<boolean>(false);

  const [folderToEdit, setFolderToEdit] = useState<FlowFolderListItem>();
  const [editFolderModalOpen, setEditFolderModalOpen] =
    useState<boolean>(false);

  const [folderToDelete, setFolderToDelete] = useState<FlowFolderListItem>();
  const [deleteFolderModalOpen, setDeleteFolderModalOpen] =
    useState<boolean>(false);

  const onRequestDeleteFolder = (folder: FlowFolderListItem) => {
    setFolderToDelete(folder);
    setDeleteFolderModalOpen(true);
  };

  const onCopyLink = (folder: FlowFolderListItem | undefined) => {
    copyTextToClipboard(
      getBaseUrl() +
        getUrlToWsDashboard({
          orgId: organizationId,
          wsId: workspaceId,
          page: WsDashboardPageCode.Flows,
          folderId: folder?.id,
        }),
    ).then(() => {
      toastActions.success({
        title: "Link copied to clipboard",
      });
    });
  };

  const onRequestSelect = (folderId: string) => {
    setSelectedFolderParam(folderId);
  };

  useEffect(() => {
    const getFirstNonEmptyFolder = () =>
      folderResult?.data?.folders.find((folder) => folder.flow_count > 0)?.id;

    const goToFirstNonEmptyFolder = () => {
      if (folderResult.data) {
        const folderId = getFirstNonEmptyFolder() ?? NO_FOLDER_FILTER;
        setSelectedFolderParam(folderId);
        setLatestViewedFolder(folderId);
      }
    };

    if (!selectedFolderParam && folderResult.data) {
      if (
        latestViewedFolder &&
        folderResult.data.folders.some(
          (folder) => folder.id === latestViewedFolder,
        )
      ) {
        setSelectedFolderParam(latestViewedFolder);
      } else {
        goToFirstNonEmptyFolder();
      }
    }
    if (selectedFolderParam && selectedFolderParam !== latestViewedFolder) {
      setLatestViewedFolder(selectedFolderParam);
    }
  }, [
    selectedFolderParam,
    folderResult.data,
    latestViewedFolder,
    setSelectedFolderParam,
    folderResult,
    setLatestViewedFolder,
  ]);

  const styles = listVariantStyles[variant];
  const [tooltipsClosed, setTooltipsClosed] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  useEventListener("scroll", () => setTooltipsClosed(true), listRef);

  const addFolder = (
    <AddFolder
      variant={variant}
      onRequestAddFolder={() => {
        setCreateFolderModalOpen(true);
      }}
    />
  );

  return (
    <div className={styles.container}>
      {folderResult.isLoading && variant === "sidebar" && (
        <FolderListSkeletonSidebar />
      )}
      {folderResult.isLoading && variant !== "sidebar" && (
        <FolderListSkeletonDefault />
      )}
      {!folderResult.isLoading && (
        <>
          <ul ref={listRef} className={styles.list}>
            {folderResult.data?.folders.map((folder: FlowFolderListItem) => (
              <Folder
                key={folder.id}
                folder={folder}
                selected={folder.id === selectedFolderParam}
                tooltipShouldBeClosed={tooltipsClosed}
                setTooltipShouldBeClosed={setTooltipsClosed}
                to={getUrlToWsDashboard({
                  orgId: organizationId,
                  wsId: workspaceId,
                  page: WsDashboardPageCode.Flows,
                  folderId: folder.id,
                })}
                variant={variant}
                onCopyLink={onCopyLink}
                onRequestDelete={onRequestDeleteFolder}
                onRequestEdit={() => {
                  setFolderToEdit(folder);
                  setEditFolderModalOpen(true);
                }}
                onRequestSelect={onRequestSelect}
              />
            ))}
            <Folder
              count={folderResult.data?.flows_outside_folders_count ?? 0}
              folder={undefined}
              selected={selectedFolderParam === NO_FOLDER_FILTER}
              tooltipShouldBeClosed={tooltipsClosed}
              setTooltipShouldBeClosed={setTooltipsClosed}
              to={getUrlToWsDashboard({
                orgId: organizationId,
                wsId: workspaceId,
                page: WsDashboardPageCode.Flows,
                folderId: null,
              })}
              variant={variant}
              onCopyLink={onCopyLink}
              onRequestDelete={onRequestDeleteFolder}
              onRequestSelect={() => {
                onRequestSelect(NO_FOLDER_FILTER);
              }}
            />
            {flowFolders.canCreate && variant === "default" && addFolder}
          </ul>
        </>
      )}
      {flowFolders.canCreate && variant === "sidebar" && addFolder}
      <EditFolderModal
        mode="create"
        open={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
        onConfirm={async (name: string) => {
          const response = await createFolderMutations.mutateAsync({
            name,
            workspace_id: workspaceId,
          });
          navigate(
            getUrlToWsDashboard({
              orgId: organizationId,
              wsId: workspaceId,
              page: WsDashboardPageCode.Flows,
              folderId: response.data.id,
            }),
          );
        }}
      />
      <EditFolderModal
        afterLeave={() => {
          setFolderToEdit(undefined);
        }}
        folder={folderToEdit}
        mode="edit"
        open={editFolderModalOpen}
        onClose={() => setEditFolderModalOpen(false)}
        onConfirm={async (name: string) => {
          if (folderToEdit) {
            await editFolderMutations.mutateAsync({
              folderId: folderToEdit.id,
              flowFolderUpdate: { name },
            });
          }
        }}
      />
      <DeleteFolderModal
        afterLeave={() => {
          setFolderToDelete(undefined);
        }}
        folder={folderToDelete}
        isOpen={deleteFolderModalOpen}
        onClose={() => {
          setDeleteFolderModalOpen(false);
        }}
        onConfirm={wrapWithAxiosResponseErrorHandler(async () => {
          if (!folderToDelete) return;

          await deleteFolderMutations.mutateAsync(folderToDelete.id);
          setSelectedFolderParam(NO_FOLDER_FILTER);
          setDeleteFolderModalOpen(false);
        })}
      />
    </div>
  );
};
